import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import StorefrontApiService from '@services/StorefrontApiService';
import { base64Encode } from '@services/Utils';
import { StaticLabelsResponse, StorefrontDeliveryTime } from '@pages/api/properties/labels';
import { ShopwareUuid } from '@services/types/Shopware';

// Define types
interface Notification {
    id: string;
    type?: string;
    message?: string | null;
    removing: boolean;
    timestamp: Date;
    clearTimeoutId: ReturnType<typeof setTimeout>;
    title?: string | null;
    titleData?: object | null;
    messageData?: object | null;
    components?: JSX.Element;
    onClick?: () => void,
    [key: string]: any;
}

interface ApplicationState {
    deliveryTimes: StaticLabelsResponse['deliveryTimes'];
    marketingLabels: StaticLabelsResponse['marketingLabels'];
    surrogateKeys: string[];
    notifications: Notification[];
    isMobile: boolean;
    modals: {
        cartDrawerOpen: boolean,
        menuDrawerOpen: boolean,
        desktopMenuOpen: boolean,
        searchOpen: boolean,
        accountDrawerOpen: boolean,
        wishlistDrawerOpen: boolean,
    },
    methods: {
        isDevelopment: () => boolean;
        warmupStaticProperties: () => Promise<void>;
        setSurrogateKeys: (keys: string[]) => void;
        getMarketingLabels: (propertyIds: ShopwareUuid[]) => string[];
        getDeliveryTime: (id: ShopwareUuid) => StorefrontDeliveryTime | null;
        removeNotification: (id: string, delay?: number) => ReturnType<typeof setTimeout>;
        notify: (msg: string | null, options?: Partial<Notification>) => void;
        toggleDrawer: <K extends keyof ApplicationState['modals']>(key: K, open?: boolean) => void;
    };
}

const api = new StorefrontApiService();

const useApplicationStore = create<ApplicationState>()(
    persist(
        (set, get) => ({
            deliveryTimes: {
                mapped: {},
                list: [],
            },
            marketingLabels: {},
            surrogateKeys: [],
            notifications: [],
            modals: {
                cartDrawerOpen: false,
                menuDrawerOpen: false,
                desktopMenuOpen: false,
                searchOpen: false,
                accountDrawerOpen: false,
                wishlistDrawerOpen: false,
            },
            isMobile: false,
            methods: {
                toggleDrawer(key, open) {
                    set((state) => {
                        let value = !state.modals[key];
                        if (open !== undefined) {
                            value = open;
                        }
                        const otherModals = Object.keys(state.modals).reduce((acc, modalKey) => ({
                            ...acc,
                            [modalKey]: false
                        }), state.modals);

                        const modals = {
                            ...otherModals,
                            [key]: value,
                        };
                        window.dispatchEvent(new Event("toggleDrawer"));
                        return { modals };
                    });
                },
                isDevelopment() {
                    return ['localhost', 'stage', '.int', '.local'].some(term => location.href.includes(term));

                },
                async warmupStaticProperties() {
                    const labels = await api.fetchStaticLabels();

                    set({
                        marketingLabels: labels.marketingLabels,
                        deliveryTimes: labels.deliveryTimes,
                    });

                    if (typeof window !== 'undefined') {
                        const isMobile = window.innerWidth < 1024;
                        set({isMobile})
                    }
                },
                setSurrogateKeys(keys) {
                    set({ surrogateKeys: keys });
                },
                getDeliveryTime(id: ShopwareUuid) {
                    return get().deliveryTimes?.list.find(item => item.id === id) || null;
                },
                getMarketingLabels(propertyIds) {
                    const labels = get().marketingLabels;
                    return propertyIds?.filter((id) => labels?.[id]).map((id) => {
                        return labels[id];
                    }) || [];
                },
                removeNotification(id, delay = 0) {
                    return setTimeout(function() {
                        set({
                            notifications: get().notifications.map(n => {
                                if (n.id === id) {
                                    n.removing = true;
                                }
                                return n;
                            }),
                        });

                        setTimeout(() => {
                            set({ notifications: get().notifications.filter(n => n.id !== id) });
                        }, 500);
                    }, delay);
                },
                notify(msg, options: Partial<Notification> = {}) {
                    const state = get();
                    const id = options?.id ? options.id : base64Encode((new Date()).getTime().toString());
                    const message = {
                        type: 'standard',
                        ...options,
                        id,
                        message: msg,
                        removing: false,
                        timestamp: new Date(),
                        clearTimeoutId: state.methods.removeNotification(id, 7000),
                    };

                    set({ notifications: [message, ...get().notifications] });
                },
            },
        }),
        {
            name: 'app-storage',
            partialize: (state) => ({
                marketingLabels: state.marketingLabels,
                deliveryTimes: state.deliveryTimes,
            }),
        },
    ),
);

export default useApplicationStore;
